import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Form } from 'react-final-form';
import { Button } from '@/shared/components/Button';
import { Card, CardBody } from '@/shared/components/Card';
import { FormButtonToolbar, FormContainer } from '@/shared/components/form/FormElements';
import { formStructure, formSections } from './formConfig';
import { gerarLinkComTokenCriptografado, gerarLinkAcessoDireto } from '../../../../../utils/gerarLinkComTokenCriptografado';
import DynamicForm from '../../../../../shared/components/form/DynamicForm';
import CopyButton from '../../../../../shared/components/CopyLink';
import PageHeader from '../../../../../shared/components/HeaderForm';
import ConfirmDeleteModal from '../../../../UI/Modals/index2';
import { useAuth } from '../../../../../context/AuthContext';
import { get, put, deleteRequest } from '@/utils/api/base';
import { dataFormatada } from '../../../../../utils/dataFormatada'

const mapApiToForm = (dados) => {
    // Calcular dias parados com base na data atual
    const calcularDiasParados = (dataDisponibilidade) => {
        if (!dataDisponibilidade) return 0;

        const dataAtual = new Date();
        const dataDisponibilidadeObj = new Date(dataDisponibilidade);
        const diferencaMs = dataAtual.getTime() - dataDisponibilidadeObj.getTime();
        const diferencaDias = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
        return Math.max(0, diferencaDias);
    };

    return {
        frota: dados.Frota || '',
        segmento: 'Não informado',
        destino: dados.Destino || '',
        responsavelComercial: dados.NomeComercial || '',
        uf: dados.LocalEstado || '',
        diasParados: calcularDiasParados(dados.DataDisponibilidade),
        DataDisponibilidade: dataFormatada(dados.DataDisponibilidade) || '',
        gestor: dados.NomeGestor || '',
        vazioem: dados.LocalCidade || '',
        vazio: dados.Vazio === 1,
        coleta: dados.Coleta === 1,
        negociacao: dados.Negociacao === 1,
    };
};


const mapFormToApi = (values) => {
    // Formatar a data do formulário para o formato da API (YYYY-MM-DD)
    const formatarDataParaAPI = (data) => {
        if (!data) {
            // Se não houver data, usar data atual
            return new Date().toISOString().slice(0, 19).replace('T', ' ');
        }
        
        // Se for um objeto Date, converter para string
        if (data instanceof Date) {
            return data.toISOString().slice(0, 19).replace('T', ' ');
        }
        
        // Se for uma string, tentar converter
        try {
            const dataObj = new Date(data);
            return dataObj.toISOString().slice(0, 19).replace('T', ' ');
        } catch (error) {
            console.error('Erro ao formatar data:', error);
            return new Date().toISOString().slice(0, 19).replace('T', ' ');
        }
    };

    const dataModificacao = new Date().toISOString().slice(0, 19).replace('T', ' ');
    return {
        DataDisponibilidade: formatarDataParaAPI(values.DataDisponibilidade),
        LocalCidade: values.vazioem,
        LocalEstado: values.uf,
        Frota: values.frota,
        Veiculo: "ABC-1234",
        NomeGestor: values.gestor,
        Vazio: values.vazio ? 1 : 0,
        Coleta: values.coleta ? 1 : 0,
        Negociacao: values.negociacao ? 1 : 0,
        NomeComercial: values.responsavelComercial,
        Destino: values.destino,
        DataModificacao: dataModificacao,
        UsuarioModificacao: 'usuario2',
    };
};

const getDisponibilidadeById = async (id) => {
    const response = await get(`${process.env.REACT_APP_BASE_API_URL}/frotas/disponibilidade/${id}`);
    return response.data;
};

const updateDisponibilidade = async (id, payload) => {
    const response = await put(`${process.env.REACT_APP_BASE_API_URL}/frotas/disponibilidade/${id}`, payload);
    return response.data;
};

const deleteDisponibilidade = async (id) => {
    await deleteRequest(`${process.env.REACT_APP_BASE_API_URL}/frotas/disponibilidade/${id}`);
};

const Forms = () => {
    const { id } = useParams();
    const [dadosRecebidos, setDadosRecebidos] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const location = useLocation();
    const isPersonalizado = location.pathname.includes('/detalhesdisponibilidade/personalizado/');
    const { user } = useAuth();

    // Gera link básico (com token de acesso genérico)
    const linkSeguro = gerarLinkComTokenCriptografado(
        `${process.env.REACT_APP_BASE_URL}/pages/programacao/detalhesdisponibilidade/personalizado/${id}`,
        'TOKEN-ACESSO-DIRETO-TRANSBEN' // Token específico para acesso direto
    );
    
    // Gera link com token do usuário atual (se disponível)
    const linkComToken = user?.accessToken 
        ? gerarLinkAcessoDireto(
            `${process.env.REACT_APP_BASE_URL}/pages/programacao/detalhesdisponibilidade/personalizado/${id}`,
            user.accessToken,
            {
                name: user.name,
                email: user.email,
                expiresAt: user.expiresAt
            }
          )
        : linkSeguro;

    useEffect(() => {
        if (!id) return;
        getDisponibilidadeById(id)
            .then(setDadosRecebidos)
            .catch(() => alert('Erro ao carregar dados da disponibilidade.'))
            .finally(() => setLoading(false));
    }, [id]);

    const onSubmit = useCallback(async (values) => {
        try {
            const payload = mapFormToApi(values);
            await updateDisponibilidade(id, payload);
            alert('Atualização feita com sucesso!');
        } catch (err) {
            alert(`Erro ao atualizar os dados: ${err.message}`);
        }
    }, [id]);

    const handleDelete = async () => {
        try {
            await deleteDisponibilidade(id);
            alert('Disponibilidade deletada com sucesso!');
            window.location.href = '/pages/programacao/disponibilidade';
        } catch (err) {
            alert(`Erro ao deletar: ${err.message}`);
        } finally {
            setModalVisible(false);
        }
    };


    // Função para testar se o token está sendo enviado
    const testarToken = async () => {
        try {
            console.log('🧪 Testando envio de token...');
            
            // Verificar qual token está armazenado
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                console.log('👤 Usuário atual:', user);
                console.log('🔑 Token armazenado:', user.accessToken || user.token);
            }
            
            const response = await get(`${process.env.REACT_APP_BASE_API_URL}/frotas/disponibilidade/${id}`);
            console.log('✅ Requisição bem-sucedida:', response.data);
            alert('Token enviado com sucesso! Verifique o console para mais detalhes.');
        } catch (error) {
            console.error('❌ Erro na requisição:', error);
            alert(`Erro na requisição: ${error.message}`);
        }
    };

    // Função para gerar link de teste
    const gerarLinkTeste = () => {
        const tokenTeste = 'TOKEN-TESTE-TRANSBEN-123';
        const linkTeste = gerarLinkAcessoDireto(
            `http://localhost:3000/pages/programacao/detalhesdisponibilidade/personalizado/${id}`,
            tokenTeste,
            {
                name: 'Usuário Teste',
                email: 'teste@transben.com.br'
            }
        );
        
        console.log('🔗 Link de teste gerado:', linkTeste);
        console.log('🎯 Token que será usado:', tokenTeste);
        
        // Copiar para clipboard
        navigator.clipboard.writeText(linkTeste);
        alert('Link de teste copiado! Acesse o link e clique em "Testar Token" para verificar.');
    };

    const initialValues = useMemo(() => { 
        if (!dadosRecebidos) return {}; 
        return mapApiToForm(dadosRecebidos);
    }, [dadosRecebidos]);

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>⏳ Carregando dados da disponibilidade...</p>
            </div>
        );
    }

    return (
        <>
            <Form onSubmit={onSubmit} initialValues={initialValues}>
                {({ handleSubmit, form: { reset } }) => (
                    <Card>
                        <CardBody>
                            <Card>
                                <div className="d-flex justify-content-between align-items-center px-3 py-2">
                                    <PageHeader
                                        titulo={isPersonalizado ? "Edição Externa" : "Painel de Operações."}
                                        subtitulo={isPersonalizado ? "Modo de acesso direto - Edição restrita" : "Acompanhe as atividades em tempo real"}
                                    />
                                    {!isPersonalizado && (
                                        <div className="d-flex align-items-center gap-2">
                                            <CopyButton
                                                textToCopy={linkComToken}
                                                tooltip="Copiar Link com Autenticação"
                                                copiedTooltip="Link copiado!"
                                                placement="bottom"
                                                dir="ltr"
                                                iconSize="18px"
                                            />
                                            {user?.accessToken && (
                                                <CopyButton
                                                    textToCopy={linkSeguro}
                                                    tooltip="Copiar Link Básico"
                                                    copiedTooltip="Link básico copiado!"
                                                    placement="bottom"
                                                    dir="ltr"
                                                    iconSize="18px"
                                                />
                                            )}
                                        </div>
                                    )}
                                    {isPersonalizado && (
                                        <div className="d-flex align-items-center gap-2">
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm"
                                                onClick={() => window.location.href = '/log_in'}
                                                style={{ fontSize: '12px' }}
                                            >
                                                <i className="fas fa-sign-in-alt"></i> Fazer Login
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Card>
                            <FormContainer onSubmit={handleSubmit}>
                                <DynamicForm
                                    structure={formStructure}
                                    sections={formSections(initialValues)}
                                />
                                <FormButtonToolbar className="mt-5 d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                                    <div className="d-flex gap-2">
                                        <Button variant="primary" type="submit">Salvar</Button>
                                        {isPersonalizado ? (
                                            <Button variant="secondary" type="button" onClick={() => window.close()}>
                                                Fechar
                                            </Button>
                                        ) : (
                                            <a href="/pages/programacao/disponibilidade">
                                                <Button variant="secondary" type="button">Cancelar</Button>
                                            </a>
                                        )}
                                    </div>
                                    {!isPersonalizado && (
                                        <Button variant="danger" type="button" onClick={() => setModalVisible(true)}>
                                            Deletar
                                        </Button>
                                    )}
                                </FormButtonToolbar>
                            </FormContainer>
                        </CardBody>
                    </Card>
                )}
            </Form>

            <ConfirmDeleteModal
                show={modalVisible}
                onClose={() => setModalVisible(false)}
                onConfirm={handleDelete}
            />
        </>
    );
};

export default Forms;